// src/components/vocabulary/admin/ProgressOverview.tsx
import React, { useState, useEffect } from "react";
import { FaUsers, FaBook, FaTrophy, FaFire } from "react-icons/fa";

interface ProgressSummary {
  totalStudentsLearning: number;
  totalWordsLearned: number;
  totalWordsMastered: number;
  levelStats: {
    [key: string]: {
      level: string;
      totalWords: number;
      studentsLearning: number;
      wordsLearned: number;
      wordsMastered: number;
      averageWordsPerStudent: number;
      completionPercentage: number;
    };
  };
  batchProgress: {
    [key: string]: {
      batchId: number;
      batchName: string;
      level: string;
      studentsCount: number;
      expectedDayNumber: number;
      averageProgress: number;
    };
  };
}

interface BatchDetail {
  batchId: number;
  batchName: string;
  level: string;
  students: Array<{
    studentId: number;
    studentName: string;
    email: string;
    wordsLearned: number;
    wordsMastered: number;
    completionPercentage: number;
    currentStreak: number;
  }>;
}

const ProgressOverview: React.FC = () => {
  const API = process.env.REACT_APP_API_BASE_URL;
  const [summary, setSummary] = useState<ProgressSummary | null>(null);
  const [selectedBatch, setSelectedBatch] = useState<number | null>(null);
  const [batchDetail, setBatchDetail] = useState<BatchDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSummary();
  }, []);

  useEffect(() => {
    if (selectedBatch) {
      loadBatchDetail(selectedBatch);
    }
  }, [selectedBatch]);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/vocabulary/progress/summary`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
      }
    } catch (error) {
      console.error("Failed to load summary:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadBatchDetail = async (batchId: number) => {
    try {
      const res = await fetch(
        `${API}/api/admin/vocabulary/progress/batches/${batchId}`,
        {
          credentials: "include",
        }
      );
      if (res.ok) {
        const data = await res.json();
        setBatchDetail(data);
      }
    } catch (error) {
      console.error("Failed to load batch detail:", error);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-10">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#009fe3]"></div>
        <p className="mt-4 text-gray-600">Loading progress data...</p>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="text-center py-10 text-gray-500">
        No progress data available
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overall Statistics */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          Overall Statistics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl border border-blue-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <FaUsers className="text-blue-600" size={24} />
              </div>
              <div>
                <p className="text-3xl font-bold text-blue-600">
                  {summary.totalStudentsLearning}
                </p>
                <p className="text-sm text-gray-600">Students Learning</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <FaBook className="text-green-600" size={24} />
              </div>
              <div>
                <p className="text-3xl font-bold text-green-600">
                  {summary.totalWordsLearned}
                </p>
                <p className="text-sm text-gray-600">Total Words Learned</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-6 rounded-xl border border-amber-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <FaTrophy className="text-amber-600" size={24} />
              </div>
              <div>
                <p className="text-3xl font-bold text-amber-600">
                  {summary.totalWordsMastered}
                </p>
                <p className="text-sm text-gray-600">Words Mastered</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Level-wise Statistics */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          Level-wise Progress
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(summary.levelStats || {}).map(([level, stats]) => (
            <div
              key={level}
              className="bg-white border border-gray-200 rounded-xl p-6 shadow-md"
            >
              <div className="text-center mb-4">
                <span
                  className={`px-4 py-2 rounded-full text-sm font-bold ${
                    level === "A1"
                      ? "bg-green-100 text-green-700"
                      : level === "A2"
                      ? "bg-blue-100 text-blue-700"
                      : level === "B1"
                      ? "bg-orange-100 text-orange-700"
                      : "bg-purple-100 text-purple-700"
                  }`}
                >
                  {level} Level
                </span>
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Words:</span>
                  <span className="font-bold text-gray-800">
                    {stats.totalWords}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Students:</span>
                  <span className="font-bold text-gray-800">
                    {stats.studentsLearning}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Words Learned:</span>
                  <span className="font-bold text-green-600">
                    {stats.wordsLearned}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Avg per Student:</span>
                  <span className="font-bold text-blue-600">
                    {stats.averageWordsPerStudent.toFixed(1)}
                  </span>
                </div>
                <div className="pt-2 border-t border-gray-200">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-gray-600 text-xs">Completion:</span>
                    <span className="font-bold text-purple-600 text-xs">
                      {stats.completionPercentage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all"
                      style={{
                        width: `${Math.min(stats.completionPercentage, 100)}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Batch-wise Progress */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          Batch Progress
        </h2>
        <div className="space-y-4">
          {Object.entries(summary.batchProgress || {}).map(
            ([batchId, batch]) => (
              <div
                key={batchId}
                onClick={() => setSelectedBatch(batch.batchId)}
                className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow cursor-pointer"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-1">
                      {batch.batchName}
                    </h3>
                    <span className="px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-700">
                      {batch.level} Level
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-[#009fe3]">
                      {batch.averageProgress.toFixed(1)}%
                    </p>
                    <p className="text-xs text-gray-600">Avg Progress</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <p className="text-sm text-gray-600">Students</p>
                    <p className="text-lg font-bold text-gray-800">
                      {batch.studentsCount}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Current Day</p>
                    <p className="text-lg font-bold text-gray-800">
                      {batch.expectedDayNumber}
                    </p>
                  </div>
                </div>

                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-gradient-to-r from-[#009fe3] to-[#0088cc] h-3 rounded-full transition-all"
                    style={{
                      width: `${Math.min(batch.averageProgress, 100)}%`,
                    }}
                  />
                </div>
              </div>
            )
          )}
        </div>
      </div>

      {/* Batch Detail Modal */}
      {selectedBatch && batchDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-gray-800">
                  {batchDetail.batchName} - Student Progress
                </h3>
                <p className="text-sm text-gray-600">
                  {batchDetail.level} Level
                </p>
              </div>
              <button
                onClick={() => setSelectedBatch(null)}
                className="text-gray-600 hover:text-gray-800 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                        Student
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                        Learned
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                        Mastered
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                        Progress
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                        Streak
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {batchDetail.students.map((student) => (
                      <tr key={student.studentId} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-gray-800">
                              {student.studentName}
                            </p>
                            <p className="text-xs text-gray-500">
                              {student.email}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="font-bold text-green-600">
                            {student.wordsLearned}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="font-bold text-amber-600">
                            {student.wordsMastered}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-blue-500 h-2 rounded-full"
                                style={{
                                  width: `${Math.min(
                                    student.completionPercentage,
                                    100
                                  )}%`,
                                }}
                              />
                            </div>
                            <span className="text-sm font-medium text-gray-700">
                              {student.completionPercentage.toFixed(0)}%
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <FaFire className="text-orange-500" />
                            <span className="font-bold text-orange-600">
                              {student.currentStreak}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressOverview;
